import { useState, useEffect, useRef, useCallback } from 'react';
import { queryRange, queryInstant } from '../../../../api/cansense/prometheusApi';
import { getTemplatesForJob, METRIC_TEMPLATES } from '../DashboardV2/metricTemplates';
import type {
  Device,
  MetricPeriod,
  MetricPanelDef,
  MetricPanelData,
  MetricGroupData,
  MetricSeriesData,
  PrometheusRangeResult,
} from '../DashboardV2/types';
import { METRIC_PERIOD_CONFIG } from '../DashboardV2/types';

const POLL_INTERVAL = 30_000; // 30 seconds
const MAX_CONCURRENT = 4;

interface UseDeviceMetricsResult {
  groups: MetricGroupData[];
  loading: boolean;
  error: string | null;
  lastUpdated: Date | null;
}

/**
 * Resolve rate interval placeholder in a query expression.
 */
function resolveExpr(expr: string, instance: string, rateInterval: string): string {
  return expr
    .replace(/\{\{instance\}\}/g, instance)
    .replace(/\$__rate_interval/g, rateInterval);
}

/**
 * Extract legend label from a Prometheus metric result, replacing template variables
 * like {{mountpoint}} or {{groupname}} with actual label values.
 */
function resolveLegend(template: string, metric: Record<string, string>): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key) => metric[key] || key);
}

/**
 * Run promises in batches of `concurrency`.
 */
async function batchedPromises<T>(
  tasks: (() => Promise<T>)[],
  concurrency: number,
): Promise<T[]> {
  const results: T[] = [];
  for (let i = 0; i < tasks.length; i += concurrency) {
    const batch = tasks.slice(i, i + concurrency);
    const batchResults = await Promise.all(batch.map((fn) => fn()));
    results.push(...batchResults);
  }
  return results;
}

/**
 * Transform Prometheus range results into recharts-compatible data.
 * Multiple series are merged by timestamp into a single array of objects.
 */
function transformRangeData(
  results: { result: PrometheusRangeResult[]; legend: string }[],
): { series: MetricSeriesData[]; legends: string[] } {
  const timeMap = new Map<number, MetricSeriesData>();
  const legends: string[] = [];

  results.forEach(({ result: promResults, legend: legendTemplate }) => {
    promResults.forEach((promResult, idx) => {
      const legendLabel =
        promResults.length > 1
          ? resolveLegend(legendTemplate, promResult.metric)
          : legendTemplate;

      // Deduplicate legend names by appending index
      const uniqueLegend = legends.includes(legendLabel)
        ? `${legendLabel} (${idx + 1})`
        : legendLabel;
      legends.push(uniqueLegend);

      promResult.values.forEach(([ts, val]) => {
        const time = ts * 1000; // ms for recharts
        if (!timeMap.has(time)) {
          timeMap.set(time, { time });
        }
        const row = timeMap.get(time)!;
        row[uniqueLegend] = parseFloat(val);
      });
    });
  });

  const series = Array.from(timeMap.values()).sort((a, b) => a.time - b.time);
  return { series, legends };
}

/**
 * Detect OS for cansense-push devices by checking which metrics exist.
 */
async function detectPushOS(instance: string): Promise<'linux' | 'windows' | null> {
  try {
    const [nodeResp, winResp] = await Promise.all([
      queryInstant(`up{instance=~"${instance}.*",job="cansense-push"}`).catch(() => null),
      queryInstant(`windows_os_info{instance=~"${instance}.*"}`).catch(() => null),
    ]);

    if (winResp?.data?.result?.length) return 'windows';
    if (nodeResp?.data?.result?.length) return 'linux';
    return null;
  } catch {
    return null;
  }
}

/**
 * Fetch all metrics for a single panel definition.
 */
async function fetchPanelData(
  panel: MetricPanelDef,
  instance: string,
  start: number,
  end: number,
  step: string,
  rateInterval: string,
): Promise<MetricPanelData> {
  const queryResults: { result: PrometheusRangeResult[]; legend: string }[] = [];

  // For stat panels, also fetch an instant query for the current value
  let currentValue: number | undefined;

  const tasks = panel.queries.map((q) => async () => {
    const expr = resolveExpr(q.expr, instance, rateInterval);
    try {
      const resp = await queryRange(expr, start, end, step);
      queryResults.push({
        result: resp.data?.result ?? [],
        legend: q.legend,
      });
    } catch {
      queryResults.push({ result: [], legend: q.legend });
    }
  });

  await batchedPromises(tasks, MAX_CONCURRENT);

  // For stat panels, get current value from the last data point or instant query
  if (panel.chartType === 'stat') {
    try {
      const expr = resolveExpr(panel.queries[0].expr, instance, rateInterval);
      const instantResp = await queryInstant(expr);
      if (instantResp.data?.result?.length) {
        currentValue = parseFloat(instantResp.data.result[0].value[1]);
      }
    } catch {
      // Fall back to last data point
    }
  }

  const { series, legends } = transformRangeData(queryResults);

  // If we didn't get a current value from instant query, use last data point
  if (currentValue === undefined && series.length > 0 && legends.length > 0) {
    const lastPoint = series[series.length - 1];
    currentValue = lastPoint[legends[0]] as number;
  }

  return { def: panel, series, legends, currentValue };
}

/**
 * Group panel data by section name.
 */
function groupPanels(panels: MetricPanelData[]): MetricGroupData[] {
  const groupMap = new Map<string, MetricPanelData[]>();

  panels.forEach((panel) => {
    const group = panel.def.group;
    if (!groupMap.has(group)) {
      groupMap.set(group, []);
    }
    groupMap.get(group)!.push(panel);
  });

  return Array.from(groupMap.entries()).map(([group, panels]) => ({
    group,
    panels,
  }));
}

export function useDeviceMetrics(
  device: Device | null,
  period: MetricPeriod,
): UseDeviceMetricsResult {
  const [groups, setGroups] = useState<MetricGroupData[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);
  const abortRef = useRef(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchMetrics = useCallback(async () => {
    if (!device?.host || !device?.prometheusJob) {
      setGroups([]);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    abortRef.current = false;

    try {
      const config = METRIC_PERIOD_CONFIG[period];
      const end = Math.floor(Date.now() / 1000);
      const start = end - config.seconds;
      const { step } = config;

      // Determine rate interval (same as step for simplicity)
      const rateInterval = step;

      // Resolve templates
      let templates: MetricPanelDef[] | null = getTemplatesForJob(device.prometheusJob);

      // For push exporters, detect OS
      if (device.prometheusJob === 'cansense-push') {
        const os = await detectPushOS(device.host);
        if (os === 'windows') {
          templates = METRIC_TEMPLATES.windows_exporter;
        } else if (os === 'linux') {
          templates = METRIC_TEMPLATES.node_exporter;
        }
      }

      if (!templates || templates.length === 0) {
        setGroups([]);
        setLoading(false);
        return;
      }

      if (abortRef.current) return;

      // Fetch all panels in batches
      const panelTasks = templates.map(
        (panel) => () =>
          fetchPanelData(panel, device.host, start, end, step, rateInterval),
      );

      const panelResults = await batchedPromises(panelTasks, MAX_CONCURRENT);

      if (abortRef.current) return;

      // Filter out panels with no data
      const panelsWithData = panelResults.filter(
        (p) => p.series.length > 0 || p.currentValue !== undefined,
      );

      setGroups(groupPanels(panelsWithData));
      setLastUpdated(new Date());
    } catch (err) {
      if (!abortRef.current) {
        setError(err instanceof Error ? err.message : 'Failed to fetch metrics');
      }
    } finally {
      if (!abortRef.current) {
        setLoading(false);
      }
    }
  }, [device?.host, device?.prometheusJob, period]);

  // Fetch on mount and when dependencies change
  useEffect(() => {
    fetchMetrics();

    // Set up polling
    intervalRef.current = setInterval(fetchMetrics, POLL_INTERVAL);

    return () => {
      abortRef.current = true;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [fetchMetrics]);

  return { groups, loading, error, lastUpdated };
}
